import React, { useCallback, useMemo } from 'react';
import { Link } from '@remix-run/react';
import { TentTypeV1 } from '@camp67/model';
import { tents } from '../constants/tents';
import { FormattedString, useMessages } from '../i18n/useMessages';
import Sprite from '../sprites/sprite';
import type {
  TentConfig,
  BookingFormMessages,
  BookingFormRoutes,
  CompletePaymentMessages,
  CompletePaymentPaypalMessages,
  MetadataIconType,
  BookingFormV4Messages,
  TentConfigV4,
} from '@kumbh/booking-components';
import { getOfferingBySlug } from '@kumbh/booking-components';

/**
 * Hook that prepares tent configurations for the shared BookingForm
 * Converts camp67's tent config with message keys to the expected format
 */
export function useTentConfigs(): TentConfig[] {
  const getMessage = useMessages();

  return useMemo(() => {
    return Object.values(TentTypeV1).map((tentType) => {
      const tent = tents[tentType];

      return {
        tentType,
        imageUrl: tent.imageUrl,
        imageAlt: getMessage(tent.imageAlt),
        title: getMessage(tent.title),
        displayName: tent.displayName,
        description: getMessage(tent.description),
        gallery: tent.gallery,
        hasPrivateBathroom: tent.isPrivate,
        amenities: tent.amenities.tentAmenities.map((a) => ({
          summary: getMessage(a.summary),
          detail: a.detail,
        })),
      };
    });
  }, [getMessage]);
}

/**
 * Hook that prepares all messages needed by the shared BookingForm
 */
export function useBookingFormMessages(): BookingFormMessages {
  const getMessage = useMessages();

  return useMemo(
    () => ({
      formId: 'camp67-booking-form',

      // Section titles (using FormattedString for proper i18n with bold text)
      step1Title: (
        <FormattedString
          message="camp67-reservation.form.step-1"
          components={{ strong: <strong /> }}
        />
      ),
      step2Title: (
        <FormattedString
          message="camp67-reservation.form.step-2"
          components={{ strong: <strong /> }}
        />
      ),
      step3Title: (
        <FormattedString
          message="camp67-reservation.form.step-3"
          components={{ strong: <strong /> }}
        />
      ),

      // Section messages
      section: {
        stepTitle: getMessage('camp67-reservation.form.step'),
        nextButton: getMessage('camp67-reservation.form.step.next'),
        continueButton: getMessage('camp67-reservation.form.step.continue'),
        resetButton: getMessage('camp67-reservation.form.step.reset'),
        expandTooltip: getMessage('camp67-reservation.form.step.expand'),
        collapseTooltip: getMessage('camp67-reservation.form.step.collapse'),
        disabledTooltip: getMessage('camp67-reservation.form.step.disabled.tooltip'),
        stepDisabledTooltip: getMessage('camp67-reservation.form.step.disabled.tooltip'),
        // Step 1 summary
        step1SummaryRooms: getMessage('camp67-reservation.form.step-1.summary.rooms'),
        step1SummaryRoomsEmpty: getMessage(
          'camp67-reservation.form.step-1.summary.rooms.empty',
        ),
        // Step 2 summary
        step2SummaryDates: getMessage('camp67-reservation.form.step-2.summary.dates'),
        step2SummaryDatesEmptyStart: getMessage(
          'camp67-reservation.form.step-2.summary.dates.empty-start',
        ),
        step2SummaryDatesEmptyEnd: getMessage(
          'camp67-reservation.form.step-2.summary.dates.empty-end',
        ),
        step2SummaryDatesValue: getMessage(
          'camp67-reservation.form.step-2.summary.dates.value',
        ),
        step2SummaryGuests: getMessage('camp67-reservation.form.step-2.summary.guests'),
        step2SummaryGuestsEmpty: getMessage(
          'camp67-reservation.form.step-2.summary.guests.empty',
        ),
        // Step 3 summary
        step3SummaryGuests: getMessage('camp67-reservation.form.step-3.summary.guests'),
        step3SummaryGuestsEmpty: getMessage(
          'camp67-reservation.form.step-3.summary.guests.empty',
        ),
        step3SummaryPrimary: getMessage('camp67-reservation.form.step-3.summary.primary'),
        step3SummaryPrimaryEmpty: getMessage(
          'camp67-reservation.form.step-3.summary.primary.empty',
        ),
      },

      // Carousel messages
      carousel: {
        roomViewer: getMessage('camp67-reservation.form.step-1.room-viewer'),
      },

      // Gallery item messages
      galleryItem: {
        pricePerNight: getMessage('camp67-reservation.form.step-1.price-per-night'),
        mealsIncluded: getMessage('camp67-reservation.form.step-1.meals-included'),
        privateBathroom: getMessage('camp67-reservation.form.step-1.private-bathroom'),
        sharedBathroom: getMessage('camp67-reservation.form.step-1.shared-bathroom'),
        galleryOf: getMessage('camp67-reservation.form.step-1.room-gallery'),
        addTooltip: getMessage(
          'camp67-reservation.form.step-1.summary.rooms.add.tooltip',
        ),
        subtractTooltip: getMessage(
          'camp67-reservation.form.step-1.summary.rooms.subtract.tooltip',
        ),
      },

      // Submission CTA messages
      submissionCta: {
        total: getMessage('camp67-reservation.form.submit.total'),
        room: getMessage('camp67-reservation.form.submit.room'),
        rooms: getMessage('camp67-reservation.form.submit.rooms'),
        night: getMessage('camp67-reservation.form.submit.night'),
        nights: getMessage('camp67-reservation.form.submit.nights'),
        empty: getMessage('camp67-reservation.form.submit.empty'),
        continueButton: getMessage('camp67-reservation.form.submit.continue'),
        priceShownIn: getMessage('camp67-reservation.form.submit.price-shown-in'),
        exchangeCalculated: getMessage(
          'camp67-reservation.form.submit.exchange-calculated',
        ),
      },

      // Step 2 labels
      checkInLabel: getMessage('camp67-reservation.form.step-2.check-in'),
      checkOutLabel: getMessage('camp67-reservation.form.step-2.check-out'),
      adultsLabel: getMessage('camp67-reservation.form.step-2.adult'),
      childrenLabel: getMessage('camp67-reservation.form.step-2.children'),
      childrenDetail: getMessage('camp67-reservation.form.step-2.children-detail'),

      // Step 3 labels
      guestsTitle: getMessage('camp67-reservation.form.step-3.guests'),
      guestsDisclaimer: (
        <FormattedString
          message="camp67-reservation.form.step-3.disclaimer"
          components={{
            'payment-policy-link': (
              <Link to="/payment-policy" prefetch="intent" className="underline" />
            ),
          }}
        />
      ),
      primaryContactTitle: getMessage('camp67-reservation.form.step-3.primary-contact'),
      primaryContactInfo: (
        <FormattedString
          message="camp67-reservation.form.step-3.primary-contact.inform"
          components={{
            'privacy-policy-link': (
              <Link to="/privacy-policy" prefetch="intent" className="underline" />
            ),
          }}
        />
      ),
      emailPlaceholder: getMessage(
        'camp67-reservation.form.step-3.primary-contact.email',
      ),
      phonePlaceholder: getMessage(
        'camp67-reservation.form.step-3.primary-contact.phone',
      ),

      // Guest list messages
      guestList: {
        tooManyGuests: (totalTentCapacity: number, guestCount: number) => (
          <FormattedString
            message="camp67-reservation.form.step-3.too-many-guests"
            values={{ totalTentCapacity, guestCount }}
          />
        ),
        additionalBed: getMessage('camp67-reservation.form.step-3.additional-bed'),
        enterName: getMessage('camp67-reservation.form.step-3.guests.enter-name'),
        enterAge: getMessage('camp67-reservation.form.step-3.guests.enter-age'),
        selectGender: getMessage('camp67-reservation.form.step-3.guests.enter-gender'),
        genderMale: getMessage('camp67-reservation.form.step-3.gender-male'),
        genderFemale: getMessage('camp67-reservation.form.step-3.gender-female'),
        genderNonBinary: getMessage('camp67-reservation.form.step-3.gender-non-binary'),
        genderPreferNotToSay: getMessage(
          'camp67-reservation.form.step-3.gender-prefer-not-to-say',
        ),
      },

      // Primary contact messages
      primaryContact: {
        primaryContactLabel: getMessage('camp67-reservation.form.step-3.primary-contact'),
        selectPrimaryContact: getMessage(
          'camp67-reservation.form.step-3.primary-contact.select',
        ),
      },

      // Package selection messages
      packages: {
        title: 'Choose Your Rate Plan',
        roomOnlyLabel: 'Room Only',
        roomOnlyDescription: 'Standard room rate without package inclusions',
        noPackagesAvailable: 'No packages available for the selected room type.',
      },
    }),
    [getMessage],
  );
}

/**
 * Routes used by the booking form
 */
export const bookingFormRoutes: BookingFormRoutes = {
  completePayment: '/packages/reservation/complete-payment',
  paymentPolicy: '/payment-policy',
  privacyPolicy: '/privacy-policy',
};

/**
 * Hook that prepares all messages needed by the shared CompletePayment component
 */
export function useCompletePaymentMessages(): CompletePaymentMessages {
  const getMessage = useMessages();

  return useMemo(
    () => ({
      goBack: getMessage('camp67.go-back'),
      payment: getMessage('camp67-reservation.payment'),
      ssl: getMessage('camp67.ssl'),
      total: getMessage('camp67-reservation.form.submit.total'),
      room: getMessage('camp67-reservation.form.submit.room'),
      rooms: getMessage('camp67-reservation.form.submit.rooms'),
      night: getMessage('camp67-reservation.form.submit.night'),
      nights: getMessage('camp67-reservation.form.submit.nights'),
      priceShownIn: getMessage('camp67-reservation.form.submit.price-shown-in'),
      exchangeCalculated: getMessage(
        'camp67-reservation.form.submit.exchange-calculated',
      ),
      tandc: getMessage('camp67-reservation.payment.tandc'),
      domesticAndInternational: getMessage(
        'camp67-reservation.payment.domestic-and-international',
      ),
      international: getMessage('camp67-reservation.payment.international'),
      continuePayment: getMessage('camp67-reservation.payment.continue'),
      payWith: getMessage('camp67-reservation.payment.pay-with'),
      providerAlt: getMessage('camp67-reservation.payment.provider-alt'),
      paymentDisabledTooltip: getMessage('camp67-reservation.payment.disabled'),
    }),
    [getMessage],
  );
}

/**
 * Hook that prepares all messages needed by the shared CompletePaymentPaypal component
 */
export function useCompletePaymentPaypalMessages(): CompletePaymentPaypalMessages {
  const getMessage = useMessages();

  return useMemo(
    () => ({
      title: getMessage('camp67-reservation.payment.paypal.title'),
      ssl: getMessage('camp67.ssl'),
      nearlyThere: getMessage('camp67-reservation.payment.paypal.nearly-there'),
      instructions: getMessage('camp67-reservation.payment.paypal.instructions'),
    }),
    [getMessage],
  );
}

/**
 * Hook that provides a render function for metadata icons using camp67's sprites
 */
export function useRenderIcon(): (icon: MetadataIconType) => React.ReactNode {
  return useCallback(
    (icon: MetadataIconType) => <Sprite name={icon} width="20" height="20" />,
    [],
  );
}

/**
 * Hook that prepares tent configurations for V4 BookingForm
 */
export function useTentConfigsV4(): TentConfigV4[] {
  const getMessage = useMessages();

  return useMemo(() => {
    return Object.values(TentTypeV1).map((tentType) => {
      const tent = tents[tentType];
      const offering = getOfferingBySlug(tentType);

      return {
        tentType,
        imageUrl: tent.imageUrl,
        imageAlt: getMessage(tent.imageAlt),
        title: getMessage(tent.title),
        displayName: tent.displayName,
        description: getMessage(tent.description),
        gallery: tent.gallery,
        hasPrivateBathroom: tent.isPrivate,
        capacity: offering?.capacity,
        amenities: tent.amenities.tentAmenities.map((a) => ({
          summary: getMessage(a.summary),
          detail: a.detail,
        })),
      };
    });
  }, [getMessage]);
}

/**
 * Hook that prepares all messages needed by the V4 BookingForm
 */
export function useBookingFormV4Messages(): BookingFormV4Messages {
  const getMessage = useMessages();

  return useMemo(
    () => ({
      stepIndicator: {
        packageSelection: 'Choose Package',
        roomSelection: 'Select Rooms',
        dateGuest: 'Dates & Guests',
        guestDetails: 'Guest Details',
      },

      packageSelection: {
        title: 'Choose Your Experience',
        subtitle: 'Select a package to enhance your stay',
        roomOnlyTitle: 'Room Only',
        roomOnlyDescription: 'Standard room rate without package inclusions',
        recommended: 'Recommended',
        seeAllInclusions: 'See all inclusions',
        includes: 'Includes',
      },

      roomSelection: {
        title: 'Select Your Accommodation',
        subtitle: 'Choose from our range of luxury tents',
        pricePerNight: 'per night',
        viewDetails: 'View details',
        capacity: 'Sleeps',
        mealsIncluded: getMessage('camp67-reservation.form.step-1.meals-included'),
        privateBathroom: getMessage('camp67-reservation.form.step-1.private-bathroom'),
        sharedBathroom: getMessage('camp67-reservation.form.step-1.shared-bathroom'),
        roomsSelected: 'rooms selected',
        unavailable: 'Unavailable',
      },

      dateGuest: {
        title: 'Select Dates & Guests',
        subtitle: 'Choose your check-in and check-out dates',
        checkInLabel: getMessage('camp67-reservation.form.step-2.check-in'),
        checkOutLabel: getMessage('camp67-reservation.form.step-2.check-out'),
        adultsLabel: getMessage('camp67-reservation.form.step-2.adult'),
        childrenLabel: getMessage('camp67-reservation.form.step-2.children'),
        childrenDetail: getMessage('camp67-reservation.form.step-2.children-detail'),
        selectDatesPrompt: 'Select your dates',
        nightsSelected: 'nights',
      },

      guestDetails: {
        title: 'Guest Details',
        subtitle: 'Enter details for all guests',
        guestsTitle: getMessage('camp67-reservation.form.step-3.guests'),
        guestsDisclaimer: (
          <FormattedString
            message="camp67-reservation.form.step-3.disclaimer"
            components={{
              'payment-policy-link': (
                <Link to="/payment-policy" prefetch="intent" className="underline" />
              ),
            }}
          />
        ),
        primaryContactTitle: getMessage('camp67-reservation.form.step-3.primary-contact'),
        primaryContactInfo: (
          <FormattedString
            message="camp67-reservation.form.step-3.primary-contact.inform"
            components={{
              'privacy-policy-link': (
                <Link to="/privacy-policy" prefetch="intent" className="underline" />
              ),
            }}
          />
        ),
        emailPlaceholder: getMessage(
          'camp67-reservation.form.step-3.primary-contact.email',
        ),
        phonePlaceholder: getMessage(
          'camp67-reservation.form.step-3.primary-contact.phone',
        ),
        selectPrimaryContact: getMessage(
          'camp67-reservation.form.step-3.primary-contact.select',
        ),
        guestCard: {
          enterName: getMessage('camp67-reservation.form.step-3.guests.enter-name'),
          enterAge: getMessage('camp67-reservation.form.step-3.guests.enter-age'),
          selectGender: getMessage('camp67-reservation.form.step-3.guests.enter-gender'),
          genderMale: getMessage('camp67-reservation.form.step-3.gender-male'),
          genderFemale: getMessage('camp67-reservation.form.step-3.gender-female'),
          genderNonBinary: getMessage('camp67-reservation.form.step-3.gender-non-binary'),
          genderPreferNotToSay: getMessage(
            'camp67-reservation.form.step-3.gender-prefer-not-to-say',
          ),
        },
      },

      summary: {
        packageLabel: 'Package',
        roomsLabel: 'Rooms',
        datesLabel: 'Dates',
        guestsLabel: 'Guests',
        totalLabel: getMessage('camp67-reservation.form.submit.total'),
        taxLabel: 'GST',
        grandTotalLabel: 'Total',
        perNightLabel: getMessage('camp67-reservation.form.submit.price-shown-in'),
        continueButton: 'Continue',
        proceedToPayment: getMessage('camp67-reservation.payment.continue'),
        selectRooms: 'Select rooms',
        selectDates: 'Select dates',
        enterDetails: 'Enter details',
        night: getMessage('camp67-reservation.form.submit.night'),
        nights: getMessage('camp67-reservation.form.submit.nights'),
        adult: 'adult',
        adults: 'adults',
        child: 'child',
        children: 'children',
      },
    }),
    [getMessage],
  );
}
